import { CLIENT_PAGES } from '@/constants/clientPortalPages'
import { normalizeNullableClientId } from '@/utilities/auth/authHelper'

export const ORG_DASHBOARD_FLAG = 'client-portal-org-dashboard'

export type ClientFeatureFlagContext = { kind: 'partner'; partnerId: string } | { kind: 'none' }

export function areClientFeatureFlagContextsEqual(
  left: ClientFeatureFlagContext | undefined,
  right: ClientFeatureFlagContext | undefined,
): boolean {
  if (left?.kind !== right?.kind) return false
  if (left?.kind !== 'partner' || right?.kind !== 'partner') return true
  return left.partnerId === right.partnerId
}

export type ClientPortalRouteIntent =
  | { kind: 'org'; partnerId: string }
  | { kind: 'program'; partnerId: string | null }
  | { kind: 'dashboard-default'; selectedPartnerId: string | null }
  | { kind: 'selected'; partnerId: string | null }

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/'
}

/**
 * Resolve raw Client Portal route intent without feature-flag or authorization decisions.
 * URL program context wins when both dashboard params are present because the dashboard
 * gate canonicalizes that state to the program route.
 */
export function resolveClientPortalRouteIntent({
  pathname,
  searchParams,
  selectedClientId,
}: {
  pathname: string
  searchParams: URLSearchParams | { toString(): string }
  selectedClientId: string | null | undefined
}): ClientPortalRouteIntent {
  const normalizedPathname = normalizePathname(pathname)
  const params = new URLSearchParams(searchParams.toString())
  const programId = normalizeNullableClientId(params.get('partners'))
  const orgPartnerId = normalizeNullableClientId(params.get('org'))
  const selectedPartnerId = normalizeNullableClientId(selectedClientId)

  if (normalizedPathname === CLIENT_PAGES.dashboard) {
    if (programId) return { kind: 'program', partnerId: programId }

    if (orgPartnerId) return { kind: 'org', partnerId: orgPartnerId }

    return { kind: 'dashboard-default', selectedPartnerId }
  }

  if (normalizedPathname === CLIENT_PAGES.programDashboard) {
    return { kind: 'program', partnerId: programId ?? selectedPartnerId }
  }

  return { kind: 'selected', partnerId: selectedPartnerId }
}

export function isOrgDashboardFeatureFlagEnabled(value: unknown): boolean {
  return typeof value === 'string' && value.trim().toLowerCase() === 'enabled'
}
